import {
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  OutlinedInput,
  Typography
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { nanoid } from 'nanoid';
import PropTypes from 'prop-types';
import { OPTIONS_LABEL } from '../../utils/constants';
import palette from '../../theme/palette';
import CheckFillIcon from '../../svg/CheckFillIcon';
import PlusCircleIcon from '../../svg/PlusCircleIcon';
import DeleteIcon from '../../svg/DeleteIcon';
import ImageIcon from '../../svg/ImageIcon';
import CloseIcon from '../../svg/CloseIcon';

const useStyles = makeStyles({
  optionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '5px 0'
  }
});

export default function QuestionContainer({
  number,
  removeQues,
  questionTitle,
  answers,
  updateQuestion,
  titleError,
  optionSelectionError,
  fileObj
}) {
  const classes = useStyles();
  const [question, setQuestion] = useState(questionTitle);
  const [options, setOptions] = useState(answers);
  const [selectedFile, setSelectedFile] = useState(fileObj);
  const fileInput = useRef(null);

  const updateOption = (index, value) => {
    const newOp = [...options];
    newOp[index].value = value;
    newOp[index].isError = value.trim() === '';
    setOptions(newOp);
  };

  const selectAns = (index) => {
    const newOp = [...options];
    for (let i = 0; i < newOp.length; i += 1) {
      if (i === index) {
        newOp[i].isAnswer = true;
      } else {
        newOp[i].isAnswer = false;
      }
    }
    setOptions(newOp);
    updateQuestion({
      name: question,
      isAnsNotSelected: false,
      options
    });
  };

  const onBlurHandle = () => {
    updateQuestion({
      name: question,
      isError: question.trim() === '',
      options
    });
  };

  const addOption = () => {
    const obj = {
      _id: nanoid(),
      value: '',
      isAnswer: false,
      isRemovable: true
    };
    setOptions([...options, obj]);
    updateQuestion({
      name: question,
      options: [...options, obj]
    });
  };

  const deleteOption = (i) => {
    setOptions([...options.slice(0, i), ...options.slice(i + 1)]);
    updateQuestion({
      name: question,
      options: [...options.slice(0, i), ...options.slice(i + 1)]
    });
  };

  return (
    <Card sx={{ marginTop: 2 }}>
      <input
        type="file"
        accept="image/png, image/jpeg"
        style={{ display: 'none' }}
        ref={fileInput}
        onChange={() => {
          setSelectedFile(fileInput.current.files[0]);
          updateQuestion({
            fileObj: fileInput.current.files[0]
          });
        }}
      />
      <CardHeader
        title={`Question ${number}`}
        action={
          number !== 1 && (
            <IconButton size="medium" onClick={() => removeQues()}>
              <CloseIcon width={15} height={15} color={palette.grey[600]} />
            </IconButton>
          )
        }
      />
      <CardContent>
        <OutlinedInput
          placeholder="Type your question here"
          value={question}
          error={titleError}
          onChange={(e) => setQuestion(e.target.value)}
          fullWidth
          margin="dense"
          onBlur={() => onBlurHandle()}
          endAdornment={
            <IconButton onClick={() => fileInput.current.click()}>
              <ImageIcon color="#00000099" />
            </IconButton>
          }
        />
        {selectedFile && (
          <div style={{ marginTop: 15 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {selectedFile.name}
              <IconButton
                onClick={() => {
                  setSelectedFile(null);
                  updateQuestion({
                    fileObj: null
                  });
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        )}
        {options.map((o, i) => (
          <div className={classes.optionContainer} key={i}>
            <OutlinedInput
              placeholder={`Option ${OPTIONS_LABEL[i]}`}
              value={o.value}
              fullWidth
              error={o.isError}
              margin="dense"
              onBlur={() => onBlurHandle()}
              onChange={(e) => updateOption(i, e.target.value)}
              endAdornment={
                o.isRemovable ? (
                  <IconButton onClick={() => deleteOption(i)}>
                    <DeleteIcon />
                  </IconButton>
                ) : null
              }
            />
            <IconButton style={{ marginLeft: 5 }} onClick={() => selectAns(i)}>
              <CheckFillIcon color={o.isAnswer ? palette.secondary.main : palette.grey[400]} />
            </IconButton>
          </div>
        ))}
        {optionSelectionError && <Typography color="red">Please select answer</Typography>}
        <Button
          variant="outlined"
          fullWidth
          sx={{
            marginTop: 2,
            borderStyle: 'dashed',
            display: options.length === 4 ? 'none' : ''
          }}
          onClick={() => addOption()}
        >
          <PlusCircleIcon color={palette.primary.main} style={{ marginRight: 10 }} /> Add another
          option
        </Button>
      </CardContent>
    </Card>
  );
}

QuestionContainer.propTypes = {
  number: PropTypes.number,
  questionTitle: PropTypes.string,
  answers: PropTypes.array,
  updateQuestion: PropTypes.func,
  titleError: PropTypes.bool,
  optionSelectionError: PropTypes.bool,
  fileObj: PropTypes.any,
  removeQues: PropTypes.any
};
